<template>
    <div class="sidebar" :class="{ 'is-open': isOpen }" ref="sidebar">
        <div class="sidebar-content">
            <div class="sidebar-header">
                <img src="/templates/BinaryFlow/assets/images/logo.svg" class="logo" alt="logo">
                <a @click="closeSidebar()" class="cursor-pointer mobile-only tablet-only text-black">
                    <i class="icon-close"></i>
                </a>
            </div>
            <div @click="handleContentClick">
                <ul class="category-list" v-if="menuItems.length">
                    <li v-for="(item, index) in menuItems" :key="index">
                        <a
                            :href="`/category/${item.slug}`"
                            :class="[
                                'category-item',
                                isActive(index) ? 'active' : ''
                            ]"
                            @mouseenter="toggleSubmenu(index)"
                        >
                            {{ item.name }}
                            <template v-if="item.has_children"><i class="icon-arrow_right"></i></template>
                        </a>
                        <ul class="submenu" v-if="item.has_children && isActive(index)">
                            <li v-for="subcategory in item.items" :key="subcategory.id">
                                <a :href="`/category/${subcategory.slug}`">
                                    {{ subcategory.name }}
                                </a>
                            </li>
                        </ul>
                        <ul class="submenu" v-else-if="item.has_children && isActive(index) && item.items">
                            <li><div class="skeleton-box"></div></li>
                            <li><div class="skeleton-box"></div></li>
                            <li><div class="skeleton-box"></div></li>
                        </ul>
                    </li>
                </ul>
                <ul class="category-list" v-else>
                    <li><div class="skeleton-box"></div></li>
                    <li><div class="skeleton-box"></div></li>
                    <li><div class="skeleton-box"></div></li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script>

const MENU = '.shopMenu';
const CACHE_KEY = 'sidebarMenu';
const TTL = 24 * 60 * 60 * 1000; // 24 часа

import { defineComponent } from 'vue'

export default defineComponent({
    name: 'SidebarMenu',
    data() {
        return {
            isOpen: false,       // Состояние открытия сайдбара
            activeSubmenu: null, // Индекс активного подменю
            menuItems: [],       // Данные меню
            shopMenu: false,     // DOM элемент триггера меню
            isAuth: false,       // Авторизован покупатель или нет
        };
    },
    mounted() {

        // 1. Подключаем внешнюю кнопку-триггер
        this.shopMenu = document.querySelectorAll(MENU);
        if( this.shopMenu ) {
            this.shopMenu.forEach(e => e.addEventListener('click', this.openSidebar));

            // 2. Слушатель для закрытия при клике вне сайдбара
            document.addEventListener('click', this.handleClickOutside);
        }
    },
    unmounted() {
        // Очищаем слушатели
        if( this.shopMenu ) {
            this.shopMenu.forEach(e => e.removeEventListener('click', this.openSidebar));
        }

        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {

        // Toggle сайдбара
        openSidebar() {
            this.isOpen = true;
            this.fetchMenu(); // грузим первый уровень меню
        },

        closeSidebar() {
            this.isOpen = false;
            document.removeEventListener('click', this.handleClickOutside);
        },

        // Закрытие при клике вне сайдбара
        handleClickOutside(event) {
            const sidebar = this.$el; // Текущий корневой элемент компонента
            if (
                sidebar &&
                !sidebar.contains(event.target) &&
                this.shopMenu &&
                this.isOpen
            ) {

                let tt = false
                this.shopMenu.forEach(e => {
                    if( e.contains(event.target) )
                        tt = true
                })

                if( ! tt ) {
                    this.isOpen = false; // закрываем меню
                    this.activeSubmenu = null; // Закрываем все подменю
                }
            }
        },

        // Блокируем распространение клика внутри сайдбара
        handleContentClick(event) {
            event.stopPropagation();
        },

        // Переключение подменю по mouseenter
        toggleSubmenu( index ) {

            // Не переключаем, если уже активно
            if( this.activeSubmenu === index ) return;

            const item = this.menuItems[ index ];
            if( ! item ) {
                this.activeSubmenu = null;
                return;
            }

            // если подменю уже загружено - переключаем
            if( item.items ) {
                this.activeSubmenu = index;
            } else if( item.has_children ) {
                this.fetchMenu( item.slug, index );
            } else {
                this.activeSubmenu = null; // закрываем подменю меню
            }
        },

        // Проверка активности подменю
        isActive( index ) {
            return this.activeSubmenu === index;
        },

        findKey(array, elem, targetValue) {
            if( ! Array.isArray( array ) || targetValue === undefined ) return -1;
            return array.findIndex( item => item[ elem ] === targetValue );
        },

        // Загрузка меню с сервера (по уровням)
        async fetchMenu( slug = '', index = null ) {
            try {
                if( slug === '' ) {
                    // при первой загрузке верхнего уровня из кэша тянем данные
                    const cached = this.getCachedMenu();
                    if( cached && cached.data ) {
                        this.menuItems = cached.data;
                        return;
                    }
                } else {
                    // если уже грузили позиции меню
                    if( this.menuItems.length ) {
                        // проверяем для этого пункта уже делали запрос на получение подменю
                        const k = this.findKey( this.menuItems, 'slug', slug );
                        if( k >= 0 && this.menuItems[k]?.items ) {
                            this.activeSubmenu = index; // активный элемент меню
                            return;
                        }
                    }
                }

                const response = await this.$http.post('/getmenu', JSON.stringify({ slug }));
                if(
                    ! response.data
                    || ! response.data.data
                    || ! response.data.data.menu
                ) {
                    console.error('Invalid server response:', response.data);
                    return;
                }

                const data = response.data.data;
                const key = this.findKey( this.menuItems, 'slug', slug );
                if( key >= 0 && this.menuItems[key] ) {
                    this.menuItems.splice( key, 1, {
                        ...this.menuItems[key],
                        items: data.menu
                    } );

                } else {
                    this.menuItems = data.menu;
                }

                this.saveToCache()

                this.activeSubmenu = index;
            } catch (error) {
                console.error('Error fetching menu:', error);
                // Очищаем кэш при ошибке (на случай, если он некорректен)
                if (slug === '') this.clearCache();
            }
        },

        // Получение кэшированных данных
        getCachedMenu() {
            const raw = sessionStorage.getItem( CACHE_KEY );
            if( ! raw ) return null;

            try {
                const cached = JSON.parse( raw );
                if( Date.now() - cached.timestamp > TTL ) return null;

                return {
                    data: cached.data,
                    timestamp: cached.timestamp
                };
            } catch (e) {
                return null;
            }
        },

        // сохранение в кэш
        saveToCache() {
            if( ! this.menuItems.length ) return;

            const cache = {
                data: this.menuItems,
                timestamp: Date.now()
            };

            sessionStorage.setItem( CACHE_KEY, JSON.stringify( cache ) );
        },

        // очистка кэша
        clearCache() {
            console.log('Cache cleared:', CACHE_KEY);
            sessionStorage.removeItem( CACHE_KEY );
        },
    }
});
</script>


<style scoped>

</style>